import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import _ from "lodash";

import { store } from '../redux/store';

// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { isValidToken, setSession } from './utils';
import { loadEuroCartItems } from '../redux/slices/eurocart';
import { loadPoundCartItems } from '../redux/slices/poundcart';
import { loadEvents } from '../redux/slices/events';
import { loadEntries } from '../redux/slices/entries';
import { loadMemberships } from '../redux/slices/memberships';
import { loadHorses } from '../redux/slices/horses';
import { loadPayments } from '../redux/slices/payments';
import { loadSettings } from '../redux/slices/settings';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  if (action.type === 'UPDATE') {
    const user = cloneDeep(state.user);
    Object.keys(action.payload.user).forEach(key => _.set(user, key, action.payload.user[key]));

    return {
      ...state,
      user,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function loadData() {
  store.dispatch(loadEuroCartItems());
  store.dispatch(loadPoundCartItems());
  store.dispatch(loadEvents());
  store.dispatch(loadEntries());
  store.dispatch(loadMemberships());
  store.dispatch(loadHorses());
  store.dispatch(loadPayments());
  store.dispatch(loadSettings());
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      console.debug("Initialise");
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get('/account/auth/my-account');

        const { user } = response.data;

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
          },
        });

        loadData();

      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (username, password) => {
    const response = await axios.post('/account/auth/login', {
      username,
      password,
    });
    const { accessToken, user } = response.data;

    setSession(accessToken);

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });

    loadData();

  }, []);

  // REGISTER
  const register = useCallback(async (data) => {
    const response = await axios.post('/account/auth/register', data);
    const { accessToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async (userid) => {
    const response = await axios.post('/account/auth/logout', { userid });
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const updatesettings = useCallback(async () => {
    const response = await axios.get('/account/auth/my-account');

    const { user } = response.data;
    dispatch({
      type: 'UPDATE',
      payload: {
        user,
      }
    });
  }, []);

  const updateall = useCallback(async () => {
    loadData();
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      register,
      logout,
      updateall,
      updatesettings,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, 
      login, logout, register, updatesettings, updateall]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
